import { EyeOff } from 'lucide-react'
import { Section } from '@/shared/components/ui/Section'
import { Table } from '@/shared/components/ui/Table'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { SurvivorPickResult, type SurvivorPeerPick } from '../types'
import { PickResultBadge } from './PickResultBadge'
import styles from './SurvivorPeerPicks.module.css'

type Props = {
  picks:          SurvivorPeerPick[]
  /** True once the round's pick window has closed. */
  isLocked:       boolean
  gameweekNumber: number
}

export function SurvivorPeerPicks({ picks, isLocked, gameweekNumber }: Props) {
  const { t } = useTranslation()
  const round = t('survivor.round.short', { number: gameweekNumber })

  // Before the lock the backend only ships the caller's own pick, so there is
  // nothing to list — say why rather than showing an empty table.
  if (!isLocked) {
    return (
      <Section label={t('survivor.pick.othersTitle')} meta={round}>
        <div className={styles.hidden}>
          <EyeOff size={18} aria-hidden="true" />
          <span>{t('survivor.peers.hiddenUntilLock', { number: gameweekNumber })}</span>
        </div>
      </Section>
    )
  }

  if (picks.length === 0) {
    return (
      <Section label={t('survivor.pick.othersTitle')} meta={round}>
        <p className={styles.empty}>{t('survivor.peers.empty')}</p>
      </Section>
    )
  }

  return (
    <Section label={t('survivor.pick.othersTitle')} meta={round}>
      <Table label={t('survivor.pick.othersTitle')}>
        <tbody>
          {picks.map((pick) => {
            const isForfeit = pick.result === SurvivorPickResult.Forfeit || !pick.teamCode
            return (
              <Table.Row key={pick.participationId}>
                <Table.Cell className={styles.nameCell}>{pick.displayName}</Table.Cell>
                <Table.Cell isMuted>
                  {isForfeit ? (
                    <span className={styles.noPick}>{t('survivor.pick.none')}</span>
                  ) : (
                    pick.teamName
                  )}
                </Table.Cell>
                <Table.Cell className={styles.resultCell}>
                  <PickResultBadge
                    result={pick.result}
                    {...(pick.liveResult !== undefined ? { liveResult: pick.liveResult } : {})}
                  />
                </Table.Cell>
              </Table.Row>
            )
          })}
        </tbody>
      </Table>
    </Section>
  )
}
